import { useState, useEffect, useCallback } from 'react'
import { blogMetadataCache, type BlogCategory, type BlogTag } from '@/services/blogMetadataCache'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'

interface UseBlogMetadataReturn {
  categories: BlogCategory[]
  tags: BlogTag[]
  loading: boolean
  error: Error | null
  refresh: () => Promise<void>
  invalidateCategories: () => void
  invalidateTags: () => void
  invalidateAll: () => void
}

export function useBlogMetadata(): UseBlogMetadataReturn {
  const { user } = useAuth()
  const [categories, setCategories] = useState<BlogCategory[]>([])
  const [tags, setTags] = useState<BlogTag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchMetadata = useCallback(async (forceRefresh = false) => {
    // Only fetch if user is authenticated
    if (!user) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { categories: fetchedCategories, tags: fetchedTags } =
        await blogMetadataCache.getBoth(forceRefresh)

      setCategories(fetchedCategories)
      setTags(fetchedTags)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch blog metadata')
      setError(error)
      console.error('Failed to fetch blog metadata:', error)

      if (!forceRefresh) {
        toast.error('Failed to load categories and tags')
      }
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchMetadata()
  }, [fetchMetadata])

  const refresh = useCallback(async () => {
    await fetchMetadata(true)
  }, [fetchMetadata])

  const invalidateCategories = useCallback(() => {
    blogMetadataCache.invalidateCategories()
  }, [])

  const invalidateTags = useCallback(() => {
    blogMetadataCache.invalidateTags()
  }, [])

  const invalidateAll = useCallback(() => {
    blogMetadataCache.invalidateAll()
  }, [])

  return {
    categories,
    tags,
    loading,
    error,
    refresh,
    invalidateCategories,
    invalidateTags,
    invalidateAll,
  }
}